import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import clientService from '../services/clientService';
import CandidatePreviewPanel from './CandidatePreviewPanel';
import ClientStageManager from './ClientStageManager';
import AddCandidateModal from './AddCandidateModal';
import ClientHeader from './ClientHeader';
import DraggableMiniSidebar from './DraggableMiniSidebar';
import useSidebarOrder from '../hooks/useSidebarOrder';
import useSSE from '../hooks/useSSE';
import StatusReport from './StatusReport';
import MeetingScheduler from './MeetingScheduler';
import { exportCandidatesToCSV } from '../utils/exportUtils';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { hasPermission } from '../utils/permissions';

const API = API_BASE_URL;

const ClientPipelineView = () => {
  const { pipelineId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active');
  const [pipeline, setPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [disqualified, setDisqualified] = useState([]);
  const [placed, setPlaced] = useState([]);
  const [placedStage, setPlacedStage] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [dqSort, setDqSort] = useState({ field: null, dir: 'asc' });
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showStageManager, setShowStageManager] = useState(false);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditingJob, setIsEditingJob] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', location: '', compensation_range: '', description: '' });
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateSort, setCandidateSort] = useState('name');
  const [addingToPipeline, setAddingToPipeline] = useState(null);
  const [cardMeetings, setCardMeetings] = useState({});
  const [meetingRefresh, setMeetingRefresh] = useState(0);
  const [schedulerCard, setSchedulerCard] = useState(null);
  // Disqualify drop-zone confirmation: { cardId, name } while open, null when closed
  const [dqPrompt, setDqPrompt] = useState(null);
  const [dqReason, setDqReason] = useState('');
  const [dqSubmitting, setDqSubmitting] = useState(false);

  const [savedTabOrder, setSavedTabOrder] = useSidebarOrder('client_pipeline');
  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadPipeline(); }, [pipelineId]);

  // (Removed: All Candidates tab no longer loads cross-search client DB.
  // Use the "+ Add Candidate" modal to bring new candidates into this pipeline.)

  const handleAddToPipeline = async (candidateId) => {
    setAddingToPipeline(candidateId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/candidates/${candidateId}/add-to-pipeline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pipeline_id: parseInt(pipelineId) })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to add'); }
      await loadPipeline();
    } catch (err) {
      console.error('Error adding to pipeline:', err);
      alert(err.message || 'Failed to add candidate to pipeline');
    } finally { setAddingToPipeline(null); }
  };

  const loadPipeline = async () => {
    try {
      const data = await clientService.getPipeline(pipelineId);
      setPipeline(data.pipeline);
      const allCandidates = data.candidates || [];
      const clientStages = data.client_stages || [];

      const placedStageObj = clientStages.find(s => s.name === 'Placed');
      setPlacedStage(placedStageObj);

      const disqualifiedCandidates = allCandidates.filter(c => c.internal_stage_name === 'Disqualified' || c.status === 'rejected' || c.status === 'withdrew');
      const placedCandidates = placedStageObj ? allCandidates.filter(c => c.client_stage_id === placedStageObj.id && c.internal_stage_name !== 'Disqualified' && c.status !== 'rejected' && c.status !== 'withdrew') : [];
      const activeCandidates = allCandidates.filter(c =>
        c.internal_stage_name !== 'Disqualified' &&
        c.status !== 'rejected' &&
        c.status !== 'withdrew' &&
        (!placedStageObj || c.client_stage_id !== placedStageObj.id)
      );
      const unassignedCandidates = activeCandidates.filter(c => !c.client_stage_id);
      const regularStages = clientStages.filter(s => s.name !== 'Placed' && !['Rejected', 'Disqualified'].includes(s.name));
      const stagesWithCandidates = regularStages.map(stage => ({
        ...stage,
        candidates: activeCandidates.filter(c => c.client_stage_id === stage.id),
      }));

      setStages(stagesWithCandidates);
      setUnassigned(unassignedCandidates);
      setDisqualified(disqualifiedCandidates);
      setPlaced(placedCandidates);
    } catch (err) {
      console.error('Error loading pipeline:', err);
      if (err.response?.data) console.error('Server error detail:', JSON.stringify(err.response.data));
    } finally {
      setLoading(false);
    }
  };

  // Real-time updates via SSE
  useSSE(pipelineId, {
    onCardMoved: () => loadPipeline(),
    onRatingUpdated: () => loadPipeline(),
    onStatusChanged: () => loadPipeline(),
    onFeedbackAdded: () => loadPipeline(),
    onMeetingUpdated: () => { loadPipeline(); setMeetingRefresh(n => n + 1); },
    onStageChanged: () => loadPipeline(),
    onCandidateReactivated: () => loadPipeline(),
  });

  // Load meetings for all visible cards
  useEffect(() => {
    const allCards = [...unassigned, ...stages.flatMap(s => s.candidates)];
    if (allCards.length === 0) return;
    const token = localStorage.getItem('authToken');
    const loadMeetings = async () => {
      const meetingsMap = {};
      await Promise.all(allCards.map(async (c) => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/calendar/meetings/${c.card_id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) { const data = await res.json(); if (data.length > 0) meetingsMap[c.card_id] = data; }
        } catch {}
      }));
      setCardMeetings(meetingsMap);
    };
    loadMeetings();
  }, [stages, unassigned, meetingRefresh]);

  // ─── Counts ───
  const activeCount = useMemo(() => unassigned.length + stages.reduce((s, st) => s + st.candidates.length, 0), [unassigned, stages]);
  const allActiveCandidates = useMemo(() => [...unassigned, ...stages.flatMap(s => s.candidates)], [unassigned, stages]);
  const allCandidatesWithDQ = useMemo(() => [...allActiveCandidates, ...disqualified.map(c => ({ ...c, _isDisqualified: true }))], [allActiveCandidates, disqualified]);

  // All Candidates tab list: pipeline candidates + DQs only (no cross-search merge).
  // Adding from the broader client candidate DB is handled via the "+ Add Candidate"
  // modal (showAddCandidate) instead of inline rows.
  const mergedCandidates = allCandidatesWithDQ;

  // Candidates table (filtered + sorted)
  const filteredCandidates = useMemo(() => {
    let list = mergedCandidates;
    if (candidateSearch.trim()) {
      const q = candidateSearch.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.current_title || '').toLowerCase().includes(q) ||
        (c.current_company || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      switch (candidateSort) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'title': return (a.current_title || '').localeCompare(b.current_title || '');
        case 'company': return (a.current_company || '').localeCompare(b.current_company || '');
        case 'rating': return (parseFloat(getAvgRating(b)) || 0) - (parseFloat(getAvgRating(a)) || 0);
        default: return 0;
      }
    });
  }, [mergedCandidates, candidateSearch, candidateSort]);

  // ─── Sidebar ───
  const tabMeta = {
    active: { title: 'Pipeline Board', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>), count: activeCount },
    candidates: { title: 'All Candidates', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>), count: mergedCandidates.length },
    status_report: { title: 'Status Report', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>), count: 0 },
    job: { title: 'Job Description', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>), count: 0 },
    disqualified: { title: 'Disqualified', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>), count: disqualified.length },
    placed: { title: 'Placed', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>), count: placed.length },
  };

  const sidebarItems = useMemo(() => {
    const order = savedTabOrder || ['active', 'candidates', 'status_report', 'job', 'disqualified', 'placed'];
    return order.map(tab => {
      const meta = tabMeta[tab];
      if (!meta) return null;
      return { key: tab, label: null, title: meta.title, icon: meta.icon, badge: meta.count || undefined, active: activeTab === tab, onClick: () => setActiveTab(tab) };
    }).filter(Boolean);
  }, [savedTabOrder, activeTab, activeCount, disqualified.length, placed.length]);

  const handleSidebarReorder = useCallback((newItems) => { setSavedTabOrder(newItems.map(i => i.key)); }, [setSavedTabOrder]);

  // ─── Export ───
  const handleExport = useCallback(() => {
    let cands = [];
    if (activeTab === 'active') cands = allActiveCandidates;
    else if (activeTab === 'candidates') cands = mergedCandidates;
    else if (activeTab === 'disqualified') cands = disqualified;
    else if (activeTab === 'placed') cands = placed;
    if (cands.length > 0) exportCandidatesToCSV(cands, pipeline?.name || 'pipeline');
  }, [activeTab, allActiveCandidates, disqualified, placed, pipeline]);

  // ─── Drag & Drop (kanban) ───
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const cardId = parseInt(draggableId.replace('client-card-', ''));

    // Disqualify drop zone → prompt for reason, then dispatch via the status endpoint
    if (destination.droppableId === 'disqualify') {
      const card = [...unassigned, ...stages.flatMap(s => s.candidates), ...placed]
        .find(c => (c.card_id || c.id) === cardId);
      setDqReason('');
      setDqPrompt({ cardId, name: card?.name || 'this candidate' });
      return;
    }

    let newStageId = null;
    if (destination.droppableId === 'unassigned') newStageId = null;
    else if (destination.droppableId === 'placed') newStageId = placedStage?.id || null;
    else newStageId = parseInt(destination.droppableId.replace('client-stage-', ''));

    try {
      await axios.put(`${API}/api/client/cards/${cardId}/stage`, { client_stage_id: newStageId }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      await loadPipeline();
    } catch (err) {
      console.error('Error moving candidate:', err);
      loadPipeline();
    }
  };

  const confirmDisqualify = async () => {
    if (!dqPrompt) return;
    setDqSubmitting(true);
    try {
      await axios.put(`${API}/api/client/cards/${dqPrompt.cardId}/status`,
        { status: 'rejected', rejection_reason: dqReason.trim() || null },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setDqPrompt(null);
      setDqReason('');
      await loadPipeline();
    } catch (err) {
      console.error('Error disqualifying candidate:', err);
      alert('Failed to disqualify candidate');
    } finally {
      setDqSubmitting(false);
    }
  };

  const handleReactivateClient = async (cardId, name) => {
    if (!window.confirm(`Reactivate ${name}?\n\nThis will move them back to the active pipeline.`)) return;
    setReactivatingId(cardId);
    try {
      // Reset status to pending
      await axios.put(`${API}/api/client/cards/${cardId}/status`, { status: 'pending' }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      // Move back to first non-disqualified internal stage
      await axios.put(`${API}/api/client/cards/${cardId}/reactivate`, {}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      await loadPipeline();
    } catch (err) {
      console.error('Error reactivating:', err);
      alert('Failed to reactivate candidate');
    } finally {
      setReactivatingId(null);
    }
  };

  const toggleDqSort = (field) => {
    setDqSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };

  const sortedDisqualified = useMemo(() => {
    if (!dqSort.field) return disqualified;
    return [...disqualified].sort((a, b) => {
      const dir = dqSort.dir === 'asc' ? 1 : -1;
      if (dqSort.field === 'company') return ((a.current_company || '').localeCompare(b.current_company || '')) * dir;
      if (dqSort.field === 'stage') return ((a.rejected_at_stage_name || '').localeCompare(b.rejected_at_stage_name || '')) * dir;
      return 0;
    });
  }, [disqualified, dqSort]);

  const DqSortHeader = ({ field, align, children }) => (
    <th onClick={() => toggleDqSort(field)}
      className={`px-4 py-2 text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none ${align === 'center' ? 'text-center' : 'text-left'}`}>
      <div className={`flex items-center gap-0.5 ${align === 'center' ? 'justify-center' : ''}`}>
        {children}
        <svg className={`w-3 h-3 flex-shrink-0 ${dqSort.field === field ? 'text-blue-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {dqSort.field === field && dqSort.dir === 'desc'
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />}
        </svg>
      </div>
    </th>
  );

  // ─── Job Description Handlers ───
  const handleEditJob = () => {
    const toHtml = (text) => { if (!text) return ''; if (/<[a-z][\s\S]*>/i.test(text)) return text; return text.split('\n').map(l => `<p>${l || '<br>'}</p>`).join(''); };
    let combined = toHtml(pipeline.description);
    if (pipeline.requirements) { const rh = toHtml(pipeline.requirements); combined = combined ? combined + '<h3>Requirements & Qualifications</h3>' + rh : rh; }
    setEditFormData({ name: pipeline.name || '', location: pipeline.location || '', compensation_range: pipeline.compensation_range || '', description: combined });
    setIsEditingJob(true);
  };

  const handleSaveJob = async () => {
    try {
      await axios.put(`${API}/api/pipelines/${pipelineId}`, { ...editFormData, requirements: '' }, { headers: authHeaders });
      await loadPipeline();
      setIsEditingJob(false);
    } catch (err) { console.error('Error updating pipeline:', err); alert('Failed to update job description'); }
  };

  const handleCancelEdit = () => { setIsEditingJob(false); };

  const processFile = async (file) => {
    if (!file) return;
    const valid = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!valid.includes(file.type)) { alert('Please upload a PDF or Word document'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('File must be less than 10MB'); return; }
    setParsing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/api/client/parse-job-description`, fd, { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } });
      if (res.data) {
        let combined = res.data.description || '';
        if (res.data.requirements) combined = combined ? combined + '<h3>Requirements & Qualifications</h3>' + res.data.requirements : res.data.requirements;
        await axios.put(`${API}/api/pipelines/${pipelineId}`, {
          name: res.data.position_title || pipeline.name, location: res.data.location || pipeline.location,
          compensation_range: res.data.compensation || pipeline.compensation_range, description: combined || pipeline.description,
          requirements: '', job_description_file_url: res.data.file_url, job_description_file_name: res.data.file_name,
        }, { headers: authHeaders });
        await loadPipeline();
      }
    } catch (err) { console.error('Error parsing file:', err); alert('Could not parse the file.'); }
    finally { setParsing(false); }
  };

  const handleDeleteFile = async () => {
    if (!window.confirm('Delete this file?')) return;
    try { await axios.put(`${API}/api/pipelines/${pipelineId}`, { job_description_file_url: '', job_description_file_name: '' }, { headers: authHeaders }); await loadPipeline(); }
    catch (err) { console.error('Error deleting file:', err); }
  };

  // ─── Helpers ───
  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
  const getRatingColor = (r) => r >= 4 ? 'text-green-600' : r >= 3 ? 'text-yellow-600' : r >= 2 ? 'text-orange-600' : 'text-red-600';

  const getAvgRating = (candidate) => {
    const ratings = [];
    if (candidate.initial_rating) ratings.push(candidate.initial_rating);
    if (candidate.stage_ratings?.length) candidate.stage_ratings.forEach(sr => sr.rating && ratings.push(sr.rating));
    return ratings.length > 0 ? (ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(1) : null;
  };

  const getRatingDisplay = (candidate) => {
    const avg = getAvgRating(candidate);
    if (!avg) return null;
    const stars = Math.round(parseFloat(avg));
    return (
      <div className="flex items-center gap-0.5">
        {[1,2,3,4,5].map(s => (
          <span key={s} className={`text-[11px] leading-none ${s <= stars ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
        ))}
      </div>
    );
  };

  // Compact card for kanban — matches recruiter side layout
  const CandidateCard = ({ candidate, provided, snapshot }) => {
    const [imgErr, setImgErr] = React.useState(false);
    const [statusOpen, setStatusOpen] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [reasonOpen, setReasonOpen] = React.useState(null); // 'rejected' or 'withdrew'
    const [reason, setReason] = React.useState('');
    const showPic = candidate.avatar_url && !imgErr;
    const ini = getInitials(candidate.name);
    const timeInStage = (() => {
      const ts = candidate.last_updated || candidate.created_at;
      if (!ts) return null;
      const hrs = Math.floor((Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60));
      if (hrs < 1) return '<1h';
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d`;
      return `${Math.floor(days / 7)}w`;
    })();

    const handleStatusChange = async (newStatus, reasonText) => {
      setUpdating(true);
      try {
        await clientService.updateCandidateStatus(candidate.card_id || candidate.id, newStatus, reasonText || null);
      } catch (err) { console.error('Status update failed:', err); if (err.response?.data) console.error('Server detail:', err.response.data); }
      finally {
        await loadPipeline();
        setUpdating(false); setStatusOpen(false); setReasonOpen(null); setReason('');
      }
    };

    const statusConfig = {
      pending: { label: 'Pending', bg: 'bg-yellow-100 text-yellow-700', hoverBg: 'hover:bg-yellow-50' },
      approved: { label: 'Approved', bg: 'bg-green-100 text-green-700', hoverBg: 'hover:bg-green-50' },
      rejected: { label: 'Rejected', bg: 'bg-red-100 text-red-700', hoverBg: 'hover:bg-red-50' },
      withdrew: { label: 'Withdrew', bg: 'bg-purple-100 text-purple-700', hoverBg: 'hover:bg-purple-50' },
    };
    const currentStatus = statusConfig[candidate.status] || statusConfig.pending;

    return (
      <div
        ref={provided?.innerRef} {...(provided?.draggableProps || {})} {...(provided?.dragHandleProps || {})}
        onClick={() => setSelectedCandidate(candidate)}
        className={`bg-white border rounded-lg px-2.5 py-2 hover:shadow-md transition-shadow cursor-pointer ${snapshot?.isDragging ? 'shadow-lg ring-2 ring-blue-300' : ''}`}
      >
        <div className="flex items-center gap-2">
          {showPic ? (
            <img src={candidate.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-gray-200" onError={() => setImgErr(true)} />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
              {ini}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-900 truncate">{candidate.name}</p>
              {candidate.candidate_code && <span className="text-[9px] font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 flex-shrink-0">{candidate.candidate_code}</span>}
            </div>
            <p className="text-[10px] text-gray-500 truncate mt-0.5">{candidate.current_title}{candidate.current_company ? ` · ${candidate.current_company}` : ''}</p>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1.5">
                {getRatingDisplay(candidate) || <span className="text-[10px] text-gray-300">No rating</span>}
                <div className="relative">
                  <button onClick={(e) => { e.stopPropagation(); setStatusOpen(!statusOpen); setReasonOpen(null); setReason(''); }}
                    disabled={updating}
                    className={`text-[9px] font-medium px-1.5 py-0.5 rounded cursor-pointer transition-all ${currentStatus.bg} ${updating ? 'opacity-50' : 'hover:ring-1 hover:ring-offset-1 hover:ring-gray-300'}`}>
                    {updating ? '...' : currentStatus.label}
                  </button>
                  {statusOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setStatusOpen(false); }} />
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[130px] py-1">
                        {Object.entries(statusConfig).filter(([key]) => key !== candidate.status).map(([key, cfg]) => (
                          <button key={key} onClick={(e) => {
                            e.stopPropagation();
                            if (key === 'rejected' || key === 'withdrew') { setReasonOpen(key); setStatusOpen(false); }
                            else handleStatusChange(key);
                          }}
                            className={`w-full text-left px-3 py-1.5 text-xs ${cfg.hoverBg} flex items-center gap-2`}>
                            <span className={`w-2 h-2 rounded-full ${key === 'approved' ? 'bg-green-500' : key === 'rejected' ? 'bg-red-500' : key === 'withdrew' ? 'bg-purple-500' : 'bg-yellow-500'}`} />
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {candidate.creator_user_type === 'client' && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const isVisible = candidate.recruiter_visible !== false;
                      try {
                        await axios.put(`${API_BASE_URL}/api/client/cards/${candidate.card_id || candidate.id}/recruiter-visibility`, { visible: !isVisible }, { headers: authHeaders });
                        await loadPipeline();
                      } catch (err) { console.error('Visibility toggle error:', err); }
                    }}
                    title={candidate.recruiter_visible !== false ? 'Visible to recruiter — click to hide' : 'Hidden from recruiter — click to show'}
                    className={`p-0.5 rounded transition-colors ${candidate.recruiter_visible !== false ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-300 hover:bg-gray-100'}`}>
                    {candidate.recruiter_visible !== false ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    )}
                  </button>
                )}
                {timeInStage && <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{timeInStage}</span>}
              </div>
            </div>
          </div>
        </div>
        {/* Reason input for reject/withdraw */}
        {reasonOpen && (
          <div className={`mt-2 p-2 rounded-lg border space-y-1.5 ${reasonOpen === 'withdrew' ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`} onClick={(e) => e.stopPropagation()}>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={reasonOpen === 'withdrew' ? 'Reason for withdrawal (optional)...' : 'Reason for rejection (optional)...'}
              className={`w-full text-[10px] px-2 py-1 border rounded focus:outline-none focus:ring-1 resize-none bg-white ${reasonOpen === 'withdrew' ? 'border-purple-200 focus:ring-purple-400' : 'border-red-200 focus:ring-red-400'}`}
              rows={2} autoFocus />
            <div className="flex justify-end gap-1">
              <button onClick={(e) => { e.stopPropagation(); setReasonOpen(null); setReason(''); }} className="text-[9px] px-2 py-0.5 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={(e) => { e.stopPropagation(); handleStatusChange(reasonOpen, reason.trim() || null); }}
                className={`text-[9px] px-2 py-0.5 text-white rounded font-medium ${reasonOpen === 'withdrew' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}`}>
                Confirm
              </button>
            </div>
          </div>
        )}
        {/* Meeting row */}
        {(() => {
          const meetings = cardMeetings[candidate.card_id];
          const next = meetings?.find(m => new Date(m.start_time) > new Date());
          return (
            <div onClick={(e) => { e.stopPropagation(); if (hasPermission('schedule_meetings')) setSchedulerCard({ card_id: candidate.card_id, candidate_id: candidate.candidate_id, name: candidate.name, email: candidate.email }); }}
              className="mt-1.5 -mx-1 -mb-0.5 px-2 py-1 rounded-b-md bg-gray-50 border-t border-gray-100 flex items-center gap-1.5 hover:bg-blue-50 transition-colors cursor-pointer">
              <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {next ? (
                <span className="text-[9px] text-gray-600 truncate">
                  {new Date(next.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {new Date(next.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  {next.meeting_url && <span className="text-blue-500 ml-1">📹</span>}
                </span>
              ) : (
                <span className="text-[9px] text-gray-400">Schedule meeting</span>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <ClientHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <style>{`.ql-container{min-height:180px;font-size:13px;border-bottom-left-radius:.375rem;border-bottom-right-radius:.375rem}.ql-toolbar{border-top-left-radius:.375rem;border-top-right-radius:.375rem;background:#f9fafb}.ql-editor{min-height:180px}.ql-editor.ql-blank::before{color:#9ca3af;font-style:normal}.draggable-card{cursor:pointer!important}`}</style>
      <ClientHeader />

      <div className="flex-1 flex overflow-hidden">
        {/* Mini Sidebar */}
        <DraggableMiniSidebar
          items={sidebarItems}
          onReorder={handleSidebarReorder}
          onExport={activeTab !== 'job' && activeTab !== 'status_report' ? handleExport : undefined}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="bg-white border-b px-4 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Back + Name */}
              <button onClick={() => navigate('/client/dashboard')} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600" title="Back to Dashboard">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <span className="text-xs font-semibold text-gray-800 truncate max-w-[200px]" title={pipeline?.name}>{pipeline?.name}</span>

              <div className="w-px h-5 bg-gray-200" />

              <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
                {activeTab === 'job' ? 'Job Description' : activeTab === 'active' ? 'Pipeline Board' : activeTab === 'candidates' ? 'All Candidates' : activeTab === 'status_report' ? 'Status Report' : activeTab === 'disqualified' ? 'Disqualified' : 'Placed'}
              </span>

              <div className="flex-1" />

              {/* Context-specific buttons */}
              {activeTab === 'active' && (
                <>
                  <button onClick={() => setShowAddCandidate(true)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 font-medium shadow-sm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                    Add Candidate
                  </button>
                  <button onClick={() => setShowStageManager(true)} className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                    Stages
                  </button>
                </>
              )}
              {activeTab === 'candidates' && (
                <>
                  <button onClick={() => setShowAddCandidate(true)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 font-medium shadow-sm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                    Add Candidate
                  </button>
                  <div className="relative">
                    <input type="text" value={candidateSearch} onChange={(e) => setCandidateSearch(e.target.value)}
                      placeholder="Search candidates..." className="w-44 pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <select value={candidateSort} onChange={(e) => setCandidateSort(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700">
                    <option value="name">Name A-Z</option>
                    <option value="title">By Title</option>
                    <option value="company">By Company</option>
                    <option value="rating">Highest Rating</option>
                  </select>
                </>
              )}
              {activeTab === 'job' && !isEditingJob && (
                <button onClick={handleEditJob} className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Edit
                </button>
              )}
              {activeTab === 'job' && isEditingJob && (
                <>
                  <button onClick={handleCancelEdit} className="px-2.5 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSaveJob} className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                </>
              )}

              {/* Count */}
              <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                {activeTab === 'active' && <><span className="font-medium text-gray-500">{activeCount}</span> active</>}
                {activeTab === 'candidates' && <><span className="font-medium text-gray-500">{filteredCandidates.length}</span> of <span className="font-medium text-gray-500">{mergedCandidates.length}</span> candidates</>}
                {activeTab === 'disqualified' && <><span className="font-medium text-gray-500">{disqualified.length}</span> disqualified</>}
                {activeTab === 'placed' && <><span className="font-medium text-gray-500">{placed.length}</span> placed</>}
              </span>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto">

            {/* ══════ ACTIVE TAB ══════ */}
            {activeTab === 'active' && (
              <div className="p-4">
                <DragDropContext onDragEnd={handleDragEnd}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {/* Unassigned / New column */}
                    <Droppable droppableId="unassigned">
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                          className={`rounded-lg border transition-colors ${snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-blue-200 bg-blue-50/50'}`}>
                          <div className="px-3 py-2 border-b border-blue-200">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-blue-800">New for Review</span>
                              <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full font-medium">{unassigned.length}</span>
                            </div>
                          </div>
                          <div className="p-2 space-y-1.5 min-h-[120px]">
                            {unassigned.map((c, i) => (
                              <Draggable key={c.card_id || c.id} draggableId={`client-card-${c.card_id || c.id}`} index={i}>
                                {(prov, snap) => <CandidateCard candidate={c} provided={prov} snapshot={snap} />}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                            {unassigned.length === 0 && !snapshot.isDraggingOver && (
                              <p className="text-[10px] text-blue-400 text-center py-4">No new candidates</p>
                            )}
                          </div>
                        </div>
                      )}
                    </Droppable>

                    {/* Stage columns */}
                    {stages.map(stage => (
                      <Droppable key={stage.id} droppableId={`client-stage-${stage.id}`}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}
                            className={`rounded-lg border transition-colors ${snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                            <div className="px-3 py-2 border-b bg-gray-50">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-700">{stage.name}</span>
                                <span className="text-[10px] text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full font-medium">{stage.candidates.length}</span>
                              </div>
                            </div>
                            <div className="p-2 space-y-1.5 min-h-[120px]">
                              {stage.candidates.map((c, i) => (
                                <Draggable key={c.card_id || c.id} draggableId={`client-card-${c.card_id || c.id}`} index={i}>
                                  {(prov, snap) => <CandidateCard candidate={c} provided={prov} snapshot={snap} />}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              {stage.candidates.length === 0 && !snapshot.isDraggingOver && (
                                <p className="text-[10px] text-gray-400 text-center py-4">Drop here</p>
                              )}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    ))}
                  </div>

                  {/* Placed + Disqualify drop zones — side-by-side on md+, stacked on mobile */}
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Droppable droppableId="placed">
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                          className={`min-h-[200px] border-2 border-dashed rounded-lg px-4 py-3 flex flex-col items-center justify-center text-center transition-all ${
                            snapshot.isDraggingOver ? 'border-green-500 bg-green-100' : 'border-green-300 bg-green-50/50'
                          }`}>
                          <svg className="w-10 h-10 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-green-800">
                            {snapshot.isDraggingOver ? 'Drop to mark as Placed' : 'Drag here to place'}
                          </span>
                          <span className="text-[11px] text-green-700/70 mt-0.5">{placed.length} placed</span>
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>

                    <Droppable droppableId="disqualify">
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                          className={`min-h-[200px] border-2 border-dashed rounded-lg px-4 py-3 flex flex-col items-center justify-center text-center transition-all ${
                            snapshot.isDraggingOver ? 'border-red-500 bg-red-100' : 'border-red-300 bg-red-50/50'
                          }`}>
                          <svg className="w-10 h-10 text-red-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          <span className="text-sm font-medium text-red-800">
                            {snapshot.isDraggingOver ? 'Drop to disqualify' : 'Drag here to disqualify'}
                          </span>
                          <span className="text-[11px] text-red-700/70 mt-0.5">{disqualified.length} disqualified</span>
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </DragDropContext>

                {stages.length === 0 && unassigned.length === 0 && (
                  <div className="text-center py-16">
                    <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-gray-500 text-sm">No candidates yet</p>
                    <p className="text-xs text-gray-400 mt-1">Your recruitment team will add candidates shortly</p>
                  </div>
                )}
              </div>
            )}

            {/* ══════ CANDIDATES TABLE TAB ══════ */}
            {activeTab === 'candidates' && (
              filteredCandidates.length === 0 ? (
                <div className="text-center py-16">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-gray-500 text-sm">{mergedCandidates.length === 0 ? 'No candidates yet' : 'No candidates match your search'}</p>
                </div>
              ) : (
                <table className="w-full text-sm" style={{ minWidth: 'max-content' }}>
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Company</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredCandidates.map(c => {
                      const notInPipeline = c._notInPipeline;
                      const isDQ = !notInPipeline && (c._isDisqualified || c.internal_stage_name === 'Disqualified' || c.status === 'rejected' || c.status === 'withdrew');
                      const isWithdrew = c.status === 'withdrew';
                      const stageName = notInPipeline ? 'Not in Pipeline'
                        : isDQ ? (isWithdrew ? 'Withdrew' : 'Disqualified')
                        : c.client_stage_id ? stages.find(s => s.id === c.client_stage_id)?.name || '—'
                        : 'New / Unassigned';
                      return (
                        <tr key={c.card_id || c.id} onClick={() => !notInPipeline && setSelectedCandidate(c)}
                          className={`transition-colors ${notInPipeline ? 'hover:bg-gray-50 bg-gray-50/30' : isDQ ? (isWithdrew ? 'hover:bg-purple-50/50 bg-purple-50/30' : 'hover:bg-red-50/50 bg-red-50/30') : 'hover:bg-blue-50/50 cursor-pointer'}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${notInPipeline ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : getInitials(c.name)}
                              </div>
                              <span className={`text-sm font-medium ${notInPipeline ? 'text-gray-500' : isDQ ? 'text-gray-400' : 'text-gray-900'}`}>{c.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_title || '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_company || '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">{c.email || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                              notInPipeline ? 'bg-gray-100 text-gray-400 border border-dashed border-gray-300' :
                              isWithdrew ? 'bg-purple-100 text-purple-700' :
                              isDQ ? 'bg-red-100 text-red-700' :
                              !c.client_stage_id ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>{stageName}</span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {notInPipeline ? <span className="text-xs text-gray-300">—</span> :
                              getAvgRating(c) ? <span className={`text-xs font-bold ${getRatingColor(parseFloat(getAvgRating(c)))}`}>{getAvgRating(c)}★</span> : <span className="text-xs text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {notInPipeline ? <span className="text-xs text-gray-300">—</span> :
                              isDQ ? (
                              c.rejected_at_stage_name ? (
                                <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-orange-100 text-orange-700">Rejected at {c.rejected_at_stage_name}</span>
                              ) : (
                                <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600">Initial Review</span>
                              )
                            ) : c.status ? (
                              <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                                c.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                c.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }`}>{c.status}</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {notInPipeline ? (
                              <button onClick={(e) => { e.stopPropagation(); handleAddToPipeline(c.id); }}
                                disabled={addingToPipeline === c.id}
                                className="px-2.5 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1 mx-auto">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                {addingToPipeline === c.id ? 'Adding...' : 'Add to Pipeline'}
                              </button>
                            ) : isDQ && (
                              <button onClick={(e) => { e.stopPropagation(); handleReactivateClient(c.card_id || c.id, c.name); }}
                                disabled={reactivatingId === (c.card_id || c.id)}
                                className="px-2.5 py-1 text-[10px] font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50">
                                {reactivatingId === (c.card_id || c.id) ? 'Moving...' : 'Reactivate'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            )}

            {/* ══════ STATUS REPORT TAB ══════ */}
            {activeTab === 'status_report' && (
              <StatusReport pipelineId={pipelineId} />
            )}

            {/* ══════ JOB DESCRIPTION TAB ══════ */}
            {activeTab === 'job' && (
              <div className="p-6 max-w-4xl">
                {!isEditingJob ? (
                  <>
                    {/* View mode */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        {pipeline?.location && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {pipeline.location}
                          </span>
                        )}
                        {pipeline?.compensation_range && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {pipeline.compensation_range}
                          </span>
                        )}
                      </div>
                    </div>

                    {pipeline?.job_description_file_url && (
                      <div className="mb-6 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="text-sm text-gray-700">{pipeline.job_description_file_name || 'Attached Document'}</span>
                        </div>
                        <div className="flex gap-2">
                          <a href={`${API}${pipeline.job_description_file_url}`} target="_blank" rel="noopener noreferrer"
                            className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">View</a>
                          <button onClick={handleDeleteFile} className="px-2.5 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                        </div>
                      </div>
                    )}

                    {!pipeline?.job_description_file_url && (
                      <div className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-100' : 'border-gray-300 bg-gray-50'}`}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                        onDrop={(e) => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files[0]); }}>
                        {parsing ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-blue-600">Analyzing document...</span>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                            <span className="text-sm text-blue-600 hover:text-blue-700 font-medium">Upload or drag job description</span>
                            <p className="text-xs text-gray-400 mt-1">PDF or Word · Auto-fills position details</p>
                            <input type="file" onChange={(e) => processFile(e.target.files[0])} accept=".pdf,.doc,.docx" className="hidden" />
                          </label>
                        )}
                      </div>
                    )}

                    {(pipeline?.description || pipeline?.requirements) ? (
                      <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                        {pipeline.description && <div dangerouslySetInnerHTML={{ __html: pipeline.description }} />}
                        {pipeline.requirements && <div dangerouslySetInnerHTML={{ __html: pipeline.requirements }} />}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-400 text-sm">No job description yet</p>
                        <p className="text-xs text-gray-300 mt-1">Click "Edit" to add details</p>
                      </div>
                    )}
                  </>
                ) : (
                  /* Edit mode */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Position Title *</label>
                      <input type="text" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., Senior Software Engineer" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                        <input type="text" value={editFormData.location} onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., New York, NY" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Compensation Range</label>
                        <input type="text" value={editFormData.compensation_range} onChange={(e) => setEditFormData({ ...editFormData, compensation_range: e.target.value })}
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., $120,000 - $180,000" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Job Description</label>
                      <ReactQuill value={editFormData.description} onChange={(v) => setEditFormData({ ...editFormData, description: v })}
                        modules={{ toolbar: [[{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                        placeholder="Describe the role, responsibilities, and requirements..." />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════ DISQUALIFIED TAB ══════ */}
            {activeTab === 'disqualified' && (
              disqualified.length === 0 ? (
                <div className="text-center py-16">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500 text-sm">No disqualified candidates</p>
                  <p className="text-xs text-gray-400 mt-1">All candidates are still being considered</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <DqSortHeader field="company">Company</DqSortHeader>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                      <DqSortHeader field="stage" align="center">Last Stage</DqSortHeader>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedDisqualified.map(c => (
                      <tr key={c.card_id || c.id} onClick={() => setSelectedCandidate(c)} className="hover:bg-red-50/50 cursor-pointer transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">
                              {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : getInitials(c.name)}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{c.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_title || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_company || '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {getAvgRating(c) ? <span className={`text-xs font-bold ${getRatingColor(parseFloat(getAvgRating(c)))}`}>{getAvgRating(c)}★</span> : <span className="text-xs text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {c.status === 'withdrew' ? (
                            <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-purple-100 text-purple-700">Withdrew{c.rejected_at_stage_name ? ` at ${c.rejected_at_stage_name}` : ''}</span>
                          ) : c.rejected_at_stage_name ? (
                            <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-orange-100 text-orange-700">Rejected at {c.rejected_at_stage_name}</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600">Initial Review</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500 line-clamp-2">{c.rejection_reason || '—'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button onClick={(e) => { e.stopPropagation(); handleReactivateClient(c.card_id || c.id, c.name); }}
                            disabled={reactivatingId === (c.card_id || c.id)}
                            className="px-2.5 py-1 text-[10px] font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50">
                            {reactivatingId === (c.card_id || c.id) ? 'Moving...' : 'Reactivate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}


            {/* ══════ PLACED TAB ══════ */}
            {activeTab === 'placed' && (
              placed.length === 0 ? (
                <div className="text-center py-16">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">No placed candidates yet</p>
                  <p className="text-xs text-gray-400 mt-1">Drag candidates to the placed zone on the Active tab</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Company</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                      <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {placed.map(c => (
                      <tr key={c.card_id || c.id} onClick={() => setSelectedCandidate(c)} className="hover:bg-green-50/50 cursor-pointer transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-[10px] font-bold text-green-700 flex-shrink-0">
                              {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover ring-2 ring-green-200" /> : getInitials(c.name)}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{c.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_title || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{c.current_company || '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {getAvgRating(c) ? <span className={`text-xs font-bold ${getRatingColor(parseFloat(getAvgRating(c)))}`}>{getAvgRating(c)}★</span> : <span className="text-xs text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="px-2 py-0.5 text-[10px] rounded bg-green-100 text-green-700 border border-green-200">Placed</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showStageManager && (
        <ClientStageManager pipelineId={pipelineId} onClose={() => setShowStageManager(false)} onUpdate={loadPipeline} />
      )}
      {selectedCandidate && (
        <CandidatePreviewPanel
          candidateId={selectedCandidate.candidate_id || selectedCandidate.id}
          cardId={selectedCandidate.card_id || selectedCandidate.id}
          pipelineId={parseInt(pipelineId)}
          isOpen={!!selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          autoOpenFeedback={true}
        />
      )}
      {showAddCandidate && (
        <AddCandidateModal pipelineId={pipelineId} onClose={() => setShowAddCandidate(false)} onSuccess={() => { loadPipeline(); }} isClientMode={true} createOnly={activeTab === 'candidates'} />
      )}
      {schedulerCard && (
        <MeetingScheduler
          isOpen={true}
          onClose={() => setSchedulerCard(null)}
          cardId={schedulerCard.card_id}
          candidateId={schedulerCard.candidate_id}
          candidateName={schedulerCard.name}
          candidateEmail={schedulerCard.email}
          pipelineId={pipelineId}
          pipelineName={pipeline?.name || ''}
          onMeetingCreated={(meeting) => {
            setCardMeetings(prev => ({ ...prev, [schedulerCard.card_id]: [...(prev[schedulerCard.card_id] || []), meeting] }));
          }}
        />
      )}
      {/* Disqualify reason modal — opens when a card is dropped on the Disqualify zone */}
      {dqPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !dqSubmitting && setDqPrompt(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900">Disqualify {dqPrompt.name}?</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">This will move the candidate to Disqualified. The reason is shown on their record and the status report.</p>
            <textarea value={dqReason} onChange={(e) => setDqReason(e.target.value)} autoFocus
              placeholder="Reason for disqualification (optional)…"
              className="w-full text-xs px-2.5 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 resize-none" rows={3} />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setDqPrompt(null); setDqReason(''); }} disabled={dqSubmitting}
                className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDisqualify} disabled={dqSubmitting}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50">
                {dqSubmitting ? 'Disqualifying…' : 'Disqualify'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPipelineView;
